import { Banner, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction } from "react";
import { useUpdateWithNewElementsActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useUpdateWithNewElementsActionPlan.tsx";
import { useRecreateActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useRecreateActionPlan";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, useTheme } from "@mui/material";
import * as React from "react";
import { ActionPlan } from "src/api/client-api/ActionPlan/ActionPlan";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { URLHandler } from "src/services/URLHandler";
import { GoToSsqButton } from "src/components/Button/GoToSsqButton";

type Props = {
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    onSuccess?: () => void;
    actionPlan: ActionPlan;
};
export const StaleActionPlanModal = (props: Props) => {
    const { isOpen, setOpen, actionPlan } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const reinitializeActionPlan = useUpdateWithNewElementsActionPlan(actionPlan.id);
    const recreateActionPlan = useRecreateActionPlan(actionPlan.id);
    const isVersionMigrated = actionPlan.staleStatus === "version_migrated";

    const startPost = actionPlan.startSsqPost;
    // A migrated start post may miss answers the new version requires, so it has to be validated by hand first.
    const isValidationRequired = isVersionMigrated && !startPost.isLocked;

    const { selectedActionPlan } = useActionsContext();
    const { company, ssqVersion } = useCompanyMetadata();
    const startCampaignId = selectedActionPlan?.metadata.startingPost.campaign?.id;
    const questionnaireUrl =
        ssqVersion !== undefined && selectedActionPlan !== undefined && startCampaignId !== undefined ?
            URLHandler.company.questionnaireScore(
                company.id,
                ssqVersion.ssq.id,
                startCampaignId,
                selectedActionPlan.scoreId.toString(),
            )
        :   undefined;

    return (
        <Dialog open={isOpen} fullWidth maxWidth={"xs"}>
            <DialogTitle padding={0}>
                <Stack padding={theme.spacing(2)} gap={theme.spacing(2)}>
                    <Stack flexDirection={"row"} justifyContent={"space-between"}>
                        <Typography variant="h3">
                            {isVersionMigrated ?
                                t(
                                    "action_plan.stale.version_migrated_modal.title",
                                    "The questionnaire version changed. Recreate the action plan?",
                                )
                            :   t(
                                    "action_plan.stale.reinitialize_modal.title",
                                    "Your action plan data is outdated. Recalculate now?",
                                )
                            }
                        </Typography>
                    </Stack>
                    <Typography variant="body">
                        {isVersionMigrated ?
                            t(
                                "action_plan.stale.version_migrated_modal.body",
                                "The start questionnaire was migrated to a new version, so this action plan has to be recreated. You can reapply its actions on the new version, actions that no longer fit it are removed, or recreate it without actions.",
                            )
                        :   t(
                                "action_plan.stale.reinitialize_modal.body",
                                "Recalculating updates your action plan with the latest simulations. This operation can take a few moments.",
                            )
                        }
                    </Typography>
                </Stack>
            </DialogTitle>
            {!startPost.isLocked && (
                <DialogContent>
                    {isValidationRequired ?
                        <Banner
                            variant="themed"
                            colors={{
                                color: theme.custom.red[600],
                                backgroundColor: theme.custom.red[50],
                                sideBorder: theme.custom.red[600],
                            }}
                        >
                            <Stack gap={theme.spacing(2)}>
                                <Typography variant="body">
                                    {t(
                                        "action_plan.modal_stale.ssq_post_validation_required",
                                        "Validate the start questionnaire before recreating the action plan.",
                                    )}
                                </Typography>
                                <GoToSsqButton url={questionnaireUrl} />
                            </Stack>
                        </Banner>
                    :   <Alert severity="warning">
                            {t(
                                "action_plan.modal_stale.ssq_post_not_locked",
                                "Related questionnaire is not validated. It will be validated automatically after choosing one of the option below.",
                            )}
                        </Alert>
                    }
                </DialogContent>
            )}
            <DialogActions
                sx={{
                    padding: 0,
                    borderTop: `1px solid ${theme.custom.grey[600]}`,
                }}
            >
                <Stack width={"100%"} gap={theme.spacing(2)} padding={theme.spacing(4)}>
                    <Button
                        variant="primary"
                        disabled={
                            isValidationRequired || reinitializeActionPlan.isLoading || recreateActionPlan.isLoading
                        }
                        onClick={() => {
                            reinitializeActionPlan.mutate(null, {
                                onSuccess: () => {
                                    props.onSuccess?.();
                                    setOpen(false);
                                },
                            });
                        }}
                    >
                        {isVersionMigrated ?
                            t("action_plan.modal_stale.recreate_and_reapply_btn", "Recreate and reapply actions")
                        :   t("action_plan.modal_stale.reinitialize_btn", "Recalculate")}
                    </Button>
                    <Button
                        variant="secondary"
                        disabled={
                            isValidationRequired || reinitializeActionPlan.isLoading || recreateActionPlan.isLoading
                        }
                        onClick={() => {
                            recreateActionPlan.mutate(
                                { actionPlan },
                                {
                                    onSuccess: () => {
                                        props.onSuccess?.();
                                        setOpen(false);
                                    },
                                },
                            );
                        }}
                    >
                        {isVersionMigrated ?
                            t("action_plan.modal_stale.recreate_btn", "Recreate")
                        :   t("action_plan.modal_stale.delete_and_recreate", "Delete and recreate")}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
